export class PlaneController {
	constructor(options = {}) {
		this.element = options.element || window;
		this.keys = {};
		this.prevKeys = {};
		this.pointerLockEnabled = false;
		this.pointerLocked = false;
		this.primaryFireHeld = false;
		this.secondaryFireHeld = false;
		this.pendingWeaponScroll = 0;

		this.handleKeyDown = (e) => {
			this.keys[e.key.toLowerCase()] = true;
		};
		this.handleKeyUp = (e) => {
			this.keys[e.key.toLowerCase()] = false;
		};
		window.addEventListener('keydown', this.handleKeyDown);
		window.addEventListener('keyup', this.handleKeyUp);

		this.mouseDragging = false;
		this.mouseDeltaX = 0;
		this.mouseDeltaY = 0;
		this.lastMouseX = 0;
		this.lastMouseY = 0;
		this.mouseRollInput = 0;
		this.mousePitchInput = 0;

		this.handleMouseDown = (e) => {
			if (e.button === 2) {
				if (this.pointerLocked) {
					this.secondaryFireHeld = true;
					return;
				}

				if (this.pointerLockEnabled && this.element instanceof HTMLElement && this.element.contains(e.target)) {
					this.secondaryFireHeld = true;
					this.element.requestPointerLock?.();
				}
				return;
			}

			if (e.button !== 0) return;

			if (this.pointerLocked) {
				this.primaryFireHeld = true;
				return;
			}

			if (this.pointerLockEnabled && this.element instanceof HTMLElement && this.element.contains(e.target)) {
				this.element.requestPointerLock?.();
				return;
			}

			this.mouseDragging = true;
			this.lastMouseX = e.clientX;
			this.lastMouseY = e.clientY;
		};
		this.handleMouseMove = (e) => {
			if (this.pointerLocked) {
				this.mouseDeltaX += e.movementX;
				this.mouseDeltaY += e.movementY;
				return;
			}

			if (this.mouseDragging) {
				this.mouseDeltaX += e.clientX - this.lastMouseX;
				this.mouseDeltaY += e.clientY - this.lastMouseY;
				this.lastMouseX = e.clientX;
				this.lastMouseY = e.clientY;
			}
		};
		this.handleMouseUp = (e) => {
			if (e.button === 0) {
				this.primaryFireHeld = false;
				if (!this.pointerLocked) {
					this.mouseDragging = false;
				}
			}

			if (e.button === 2) {
				this.secondaryFireHeld = false;
			}
		};
		this.handleWheel = (e) => {
			if (!(this.element instanceof HTMLElement) || !this.element.contains(e.target)) return;
			if (e.deltaY === 0) return;
			e.preventDefault();
			this.pendingWeaponScroll += e.deltaY > 0 ? 1 : -1;
		};
		this.handlePointerLockChange = () => {
			this.pointerLocked = document.pointerLockElement === this.element;
			this.mouseDragging = this.pointerLocked;
			this.primaryFireHeld = false;
			if (!this.pointerLocked) {
				this.secondaryFireHeld = false;
			}
			this.mouseDeltaX = 0;
			this.mouseDeltaY = 0;
		};

		window.addEventListener('mousedown', this.handleMouseDown);
		window.addEventListener('mousemove', this.handleMouseMove);
		window.addEventListener('mouseup', this.handleMouseUp);
		window.addEventListener('wheel', this.handleWheel, { passive: false });
		document.addEventListener('pointerlockchange', this.handlePointerLockChange);

		this.input = {
			throttle: 0,
			pitch: 0,
			roll: 0,
			yaw: 0,
			boost: false,
			cameraYaw: 0,
			cameraPitch: 0,
			isDragging: false,
			fire: false,
			fireSecondary: false,
			zoom: false,
			fireFlare: false,
			weaponIndex: -1,
			toggleWeapon: false,
		};

		this.sensitivity = 0.2;
	}

	setSensitivity(value) {
		this.sensitivity = value;
	}

	setPointerLockEnabled(enabled) {
		this.pointerLockEnabled = enabled;
		if (!enabled) {
			this.exitPointerLock();
		}
	}

	requestPointerLock() {
		if (this.pointerLockEnabled && this.element instanceof HTMLElement) {
			this.element.requestPointerLock?.();
		}
	}

	exitPointerLock() {
		if (document.pointerLockElement === this.element) {
			document.exitPointerLock?.();
		}
		this.pointerLocked = false;
		this.mouseDragging = false;
		this.primaryFireHeld = false;
		this.secondaryFireHeld = false;
		this.mouseRollInput = 0;
		this.mousePitchInput = 0;
	}

	clamp(value, min, max) {
		return Math.max(min, Math.min(max, value));
	}

	update() {
		this.input.boost = !!this.keys[' '];
		this.input.isDragging = this.mouseDragging;
		
		this.input.fire = this.primaryFireHeld || !!this.keys.enter || !!this.keys.f;
		this.input.fireSecondary = this.secondaryFireHeld;
		this.input.zoom = this.secondaryFireHeld;
		this.input.fireFlare = !!this.keys.v || !!this.keys.x;
		
		this.input.toggleWeapon = this.pendingWeaponScroll !== 0 || (!!this.keys.q && !this.prevKeys.q);
		if (this.pendingWeaponScroll !== 0) {
			this.pendingWeaponScroll += this.pendingWeaponScroll > 0 ? -1 : 1;
		}

		this.input.weaponIndex = -1;
		if (this.keys['1']) this.input.weaponIndex = 0;
		if (this.keys['2']) this.input.weaponIndex = 1;

		const accelRate = 0.5;
		if (this.keys.w) {
			this.input.throttle = Math.min(1, this.input.throttle + accelRate * 0.016);
		} else if (this.keys.s) {
			this.input.throttle = Math.max(0, this.input.throttle - accelRate * 0.016);
		}

		const keyboardPitchTarget = this.keys.arrowup ? -1 : (this.keys.arrowdown ? 1 : 0);
		const keyboardRollTarget = this.keys.arrowleft ? -1 : (this.keys.arrowright ? 1 : 0);
		const yawTarget = this.keys.a ? -1 : (this.keys.d ? 1 : 0);

		if (this.mouseDragging) {
			const mouseFactor = Math.max(0.5, this.sensitivity) * 0.12;
			const mouseRoll = this.clamp(this.mouseDeltaX * mouseFactor, -1, 1);
			const mousePitch = this.clamp(this.mouseDeltaY * mouseFactor, -1, 1);
			this.mouseRollInput = this.lerp(this.mouseRollInput, mouseRoll, 0.85);
			this.mousePitchInput = this.lerp(this.mousePitchInput, mousePitch, 0.85);
			this.mouseDeltaX = 0;
			this.mouseDeltaY = 0;
		} else {
			this.mouseRollInput = this.lerp(this.mouseRollInput, 0, 0.28);
			this.mousePitchInput = this.lerp(this.mousePitchInput, 0, 0.28);
		}

		const pitchTarget = this.clamp(keyboardPitchTarget + this.mousePitchInput, -1, 1);
		const rollTarget = this.clamp(keyboardRollTarget + this.mouseRollInput, -1, 1);
		this.input.pitch = this.lerp(this.input.pitch, pitchTarget, 0.1);
		this.input.roll = this.lerp(this.input.roll, rollTarget, 0.1);
		this.input.yaw = this.lerp(this.input.yaw, yawTarget, 0.1);
		this.input.cameraYaw = 0;
		this.input.cameraPitch = 0;

		this.prevKeys = { ...this.keys };

		return this.input;
	}

	reset() {
		this.input.cameraYaw = 0;
		this.input.cameraPitch = 0;
		this.mouseDragging = false;
		this.primaryFireHeld = false;
		this.secondaryFireHeld = false;
		this.pendingWeaponScroll = 0;
		this.mouseDeltaX = 0;
		this.mouseDeltaY = 0;
		this.mouseRollInput = 0;
		this.mousePitchInput = 0;
		this.input.throttle = 0;
		this.input.pitch = 0;
		this.input.roll = 0;
		this.input.yaw = 0;
	}

	destroy() {
		this.exitPointerLock();
		window.removeEventListener('keydown', this.handleKeyDown);
		window.removeEventListener('keyup', this.handleKeyUp);
		window.removeEventListener('mousedown', this.handleMouseDown);
		window.removeEventListener('mousemove', this.handleMouseMove);
		window.removeEventListener('mouseup', this.handleMouseUp);
		window.removeEventListener('wheel', this.handleWheel, { passive: false });
		document.removeEventListener('pointerlockchange', this.handlePointerLockChange);
	}

	lerp(start, end, amt) {
		return (1 - amt) * start + amt * end;
	}
}